import { useSuspenseQuery } from '@apollo/client';
import { Badge } from '@mui/material';
import { MetaData } from '@sendbird/chat';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { ErrorBoundary } from '@sentry/react';
import cx from 'classnames';
import moment from 'moment-timezone';
import { FC, useEffect } from 'react';

import { gengql } from '@/__generated__';
import { useAuth } from '@/auth';
import SvgIcon from '@/components/Icon/SvgIcon';
import Picture from '@/components/Picture';
import { ERROR_CODES, hasOnlyErrorCode } from '@/core/api';
import { isAnonymousUser } from '@/messaging/sendbird';
import { surfaceCard } from '@/theme/colors';

import {
  findOtherMember,
  reportUnresolvableChannelMember,
  reportUnresolvableChatUser,
} from '../utils';
import s from './ChannelListItem.module.scss';
import OnlineStatus from './OnlineStatus';
import TypingIndicator from './TypingIndicator';

interface ChannelProps {
  channel: GroupChannel;
  metadata: MetaData;
  isTyping?: boolean;
  selected?: boolean;
  onSelect?: (channel: GroupChannel) => void | Promise<void>;
}

const GET_USER = gengql(/* GraphQL */ `
  query getChannelListItemUser($id: String!) {
    user(id: $id) {
      id
      profile {
        id
        name
        first_name
        last_name
        picture_url
        html_url
      }
    }
  }
`);

interface ChannelUserProps {
  userId: string;
  isOtherAnonymous: boolean;
  connectionStatus: string;
  lastMessageDate: string;
}

const ChannelUser = ({
  userId,
  isOtherAnonymous,
  connectionStatus,
  lastMessageDate,
}: ChannelUserProps): JSX.Element => {
  const { data, error: lookupError } = useSuspenseQuery(GET_USER, {
    variables: {
      id: userId,
    },
    skip: isOtherAnonymous,
    // See ChatMessage.tsx for the full rationale: a counterpart the api can no longer
    // resolve is an expected condition, so it comes back as an error, not a throw.
    errorPolicy: 'all',
  });

  // Anonymous wins over the lookup: a skipped queryRef can still surface an error left
  // on the shared cache key, and a confidential counterpart must never be relabelled
  // "User not available" because of it.
  const error = isOtherAnonymous ? undefined : lookupError;

  const isUnresolvable = !!error && hasOnlyErrorCode(error, ERROR_CODES.AUTH_USER_NOT_FOUND);

  // Reported from an effect rather than the render body: captureMessage is a side effect,
  // and a render around useSuspenseQuery can be discarded and retried.
  useEffect(() => {
    if (isUnresolvable) reportUnresolvableChatUser('channel_list', userId);
  }, [isUnresolvable, userId]);

  if (error && !isUnresolvable) throw error;

  // Distinct from the anonymous case below: a deleted counterpart is not a confidential
  // one, so it must not read as "Confidential Client".
  if (error) {
    return <UserNotAvailable lastMessageDate={lastMessageDate} />;
  }

  const user = isOtherAnonymous ? null : data?.user;
  const profile = user?.profile;

  return (
    <>
      <div className={s.picture}>
        <Picture user={profile} />
        {connectionStatus === 'online' && <OnlineStatus />}
      </div>

      <div className={s.content}>
        <div>{profile ? profile.name : 'Confidential Client'}</div>
        <div className={s.date}>{lastMessageDate}</div>
      </div>
    </>
  );
};

const UserNotAvailable = ({ lastMessageDate }: { lastMessageDate: string }) => {
  return (
    <>
      <div className={s.picture}>
        <Picture aria-label="User not available" />
      </div>

      <div className={s.content}>
        <div>User not available</div>
        <div className={s.date}>{lastMessageDate}</div>
      </div>
    </>
  );
};

const ChannelListItem: FC<ChannelProps> = ({
  channel,
  metadata,
  isTyping = false,
  selected = false,
  onSelect,
}) => {
  const { viewer } = useAuth();
  if (!viewer.id) throw new Error('Viewer must be logged in');

  // Null for channels that aren't a 2-party chat (e.g. engagement channels that slip past the
  // list filter) — skip them instead of taking down the whole page (ENG-5503).
  const m = findOtherMember(channel.members, viewer.id);
  if (!m) {
    // Silent for engagement channels (the filter doing its job); reports only when a legacy
    // chat vanishes because its counterpart is gone, so support can tell the two apart in
    // Sentry instead of guessing (ENG-5528 part B).
    reportUnresolvableChannelMember(channel);
    return null;
  }
  const isOtherAnonymous = isAnonymousUser(metadata, m.userId);
  const isAnonymous = isAnonymousUser(metadata, viewer.id);

  const handleClick = () => {
    if (onSelect) onSelect(channel);
  };

  const lastMessage = channel.lastMessage;
  const messageDate = lastMessage ? new Date(lastMessage.createdAt) : new Date();
  const format = moment(messageDate).startOf('day').isSame(moment().startOf('day'))
    ? 'h:mma'
    : 'D MMM';
  const lastMessageDate = lastMessage ? moment(messageDate).format(format) : '';

  const unreadCount = channel.unreadMessageCount;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      className={cx(s.channel, {
        [s.channelSelected]: selected,
        [s.unreadMessage]: unreadCount > 0,
      })}
    >
      <ErrorBoundary fallback={<UserNotAvailable lastMessageDate={lastMessageDate} />}>
        <ChannelUser
          userId={m.userId}
          isOtherAnonymous={isOtherAnonymous}
          connectionStatus={m.connectionStatus}
          lastMessageDate={lastMessageDate}
        />
      </ErrorBoundary>

      <div className={s.actions}>
        {unreadCount > 0 ? (
          // @ts-expect-error TS(2769) FIXME: No overload matches this call.
          <Badge
            style={{ padding: 0, verticalAlign: 'middle' }}
            badgeStyle={{
              position: 'initial',
              backgroundColor: selected ? surfaceCard : 'var(--darkGreen)',
              color: selected ? 'var(--darkGreen)' : 'var(--white)',
            }}
            badgeContent={unreadCount}
          />
        ) : isTyping ? (
          <TypingIndicator />
        ) : (
          isAnonymous && (
            <SvgIcon
              icon="incognito"
              width={25}
              height={25}
              fill={selected ? 'var(--white)' : 'var(--onSurface)'}
            />
          )
        )}
      </div>
    </div>
  );
};

export default ChannelListItem;
